import React, { useState, useEffect } from 'react';
import GlitchText from '../GlitchText';
import { getProducts } from '../../services/shopify';
import { useCart } from '../../context/CartContext';
import '../../styles/pages/ShopPage.css';
import Cart from '../Cart';
import { setViewportHeight } from '../../utils/viewport';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addToCart, cartCount } = useCart();
  const [isCartOpen, setIsCartOpen] = useState(false);
  // State to track current image index for each product
  const [currentImageIndexes, setCurrentImageIndexes] = useState({});

  // Enable scrolling for shop page
  useEffect(() => {
    // Update global flag to enable scrolling
    window.isHomePage = false;
    // Force scrolling to be enabled
    setViewportHeight(true);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsData = await getProducts();
        console.log("productsData:", productsData);
              
        setProducts(productsData);
        // Initialize current image index for each product
        const initialIndexes = {};
        productsData.forEach(product => {
          initialIndexes[product.id] = 0;
        });
        setCurrentImageIndexes(initialIndexes);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        console.error('Error details:', err.message);
        console.error('Error stack:', err.stack);
        setError('Failed to load products. Please try again later.');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Modify the existing useEffect for animation
  useEffect(() => {
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.animate-on-scroll');
      
      elements.forEach(element => {
        const elementTop = element.getBoundingClientRect().top;
        const elementVisible = 150;
        
        if (elementTop < window.innerHeight - elementVisible) {
          element.classList.add('animate-visible');
        } else {
          console.log('Element not yet visible');
        }
      });
    };

    // Add a small delay before initial animation check to ensure DOM is ready
    setTimeout(animateOnScroll, 100);
    
    // Force animation of all elements after a reasonable loading time (2 seconds)
    const forceAnimation = setTimeout(() => {
      document.querySelectorAll('.animate-on-scroll').forEach(element => {
        element.classList.add('animate-visible');
      });
    }, 2000);
    
    // Add scroll event listener
    window.addEventListener('scroll', animateOnScroll);
    
    // Clean up event listeners on component unmount
    return () => {
      window.removeEventListener('scroll', animateOnScroll);
      clearTimeout(forceAnimation);
    };
  }, [loading]); // Add loading as a dependency to re-trigger when products load

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  const handleAddToCart = (product) => {
    const variant = product.variants.edges[0].node;
    console.log("Adding to cart with variant ID:", variant.id);
    // Pass the variant ID, not the entire variant object
    addToCart(product, variant.id, 1);
    // Show a temporary notification or open the cart
    setIsCartOpen(true);
  };

  // Function to safely access nested properties
  const getVariantPrice = (variant) => {
    if (!variant || !variant.price || !variant.price.amount) return "0";
    return variant.price.amount;
  };

  // Functions to navigate between product images
  const nextImage = (productId) => {
    setCurrentImageIndexes(prevIndexes => {
      const currentIndex = prevIndexes[productId] || 0;
      const product = products.find(p => p.id === productId);
      const imagesCount = product.images.edges.length;
      return {
        ...prevIndexes,
        [productId]: (currentIndex + 1) % imagesCount
      };
    });
  };

  const prevImage = (productId) => {
    setCurrentImageIndexes(prevIndexes => {
      const currentIndex = prevIndexes[productId] || 0;
      const product = products.find(p => p.id === productId);
      const imagesCount = product.images.edges.length;
      return {
        ...prevIndexes,
        [productId]: (currentIndex - 1 + imagesCount) % imagesCount
      };
    });
  };

  return (
    <div className="pt-1 min-h-screen background-pattern">
      {/* Cart Icon */}
      <div className="cart-icon-container" onClick={toggleCart}>
        <div className="cart-icon">🛒</div>
        {cartCount > 0 && <div className="cart-count">{cartCount}</div>}
      </div>
      
      {/* Cart Overlay */}
      {isCartOpen && (
        <div className="cart-overlay">
          <div className="cart-sidebar">
            <button className="close-cart" onClick={toggleCart}>×</button>
            <Cart />
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Inverted header box */}
          <div className="shop-header-box animate-on-scroll">
            <h1 className="text-4xl font-black mb-2">
              <GlitchText 
                text="SHOP OUR KIMCHI" 
                className="inline-block inverted-glitch"
              />
            </h1>
            <p className="mb-0">SELECT YOUR WEAPON OF CHOICE</p>
          </div>
          
          {loading ? (
            <p className="text-center">Loading products...</p>
          ) : error ? (
            <p className="text-center text-red-500">{error}</p>
          ) : products.length === 0 ? (
            <p className="text-center">No products available at this time.</p>
          ) : (
            products.map(product => {
              const variant = product.variants.edges[0]?.node;
              const currentImageIndex = currentImageIndexes[product.id] || 0;
              const imagesCount = product.images.edges.length;
              const image = product.images.edges[currentImageIndex]?.node;
              
              console.log("variant:", variant);

              if (!variant) return null;
              
              const price = getVariantPrice(variant);
              // Only use compareAtPrice if it exists
              const compareAtPrice = variant.compareAtPrice ? 
              parseFloat(variant.compareAtPrice.amount) : null;

              return (
                <div key={product.id} className="product-card animate-on-scroll">
                  <div className="product-image-container">
                    {compareAtPrice && compareAtPrice > parseFloat(price) && (
                      <div className="product-badge">
                        {Math.round((1 - parseFloat(price) / compareAtPrice) * 100)}% OFF
                      </div>
                    )}
                    
                    {/* Image navigation arrows - only show if there are multiple images */}
                    {imagesCount > 1 && (
                      <>
                        <button 
                          className="image-nav-arrow image-nav-prev" 
                          onClick={(e) => {
                            e.stopPropagation();
                            prevImage(product.id);
                          }}
                          aria-label="Previous image"
                        >
                          ‹
                        </button>
                        <button 
                          className="image-nav-arrow image-nav-next" 
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage(product.id);
                          }}
                          aria-label="Next image"
                        >
                          ›
                        </button>
                      </>
                    )}
                    
                    <img 
                      src={image?.src || "/kimchi-bag.png"} 
                      alt={image?.altText || product.title} 
                      className="product-image" 
                    />
                    
                    {/* Image indicator dots - only show if there are multiple images */}
                    {imagesCount > 1 && (
                      <div className="image-dots">
                        {Array.from({ length: imagesCount }).map((_, index) => (
                          <span 
                            key={index} 
                            className={`image-dot ${index === currentImageIndex ? 'active' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentImageIndexes({
                                ...currentImageIndexes,
                                [product.id]: index
                              });
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <div className="product-info">
                    <h2 className="product-title">{product.title}</h2>
                    <div className="product-meta">
                      <div className="product-price-container">
                        {compareAtPrice && (
                          <p className="product-original-price">
                            {compareAtPrice} SEK
                          </p>
                        )}
                        <p className="product-discount-price">
                          {price} SEK
                        </p>
                      </div>
                    </div>
                    
                    <p className="product-description">
                      {product.description}
                    </p>
                    
                    <button 
                      className="add-to-cart-button"
                      onClick={() => handleAddToCart(product)}
                    >
                      ADD TO CART
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopPage; 